// src/components/CardEditor/ReferencesEditor.tsx
// Editor de texto simples para o modal de referências
// Ferramentas: negrito, itálico, sublinhado, lista com marcadores

import React, { useRef, useEffect, useCallback } from 'react';
import { Bold, Italic, Underline, List } from 'lucide-react';

interface ReferencesEditorProps {
    content: string;
    setContent: (content: string) => void;
    placeholder?: string;
}

export const ReferencesEditor: React.FC<ReferencesEditorProps> = ({
    content,
    setContent,
    placeholder = 'Ex: GUYTON, Arthur C. Tratado de Fisiologia Médica. 13ª ed. Rio de Janeiro: Elsevier, 2017.'
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const savedSelectionRef = useRef<{ start: number; end: number } | null>(null);

    // Inicializa o conteúdo apenas na montagem — DOM é a fonte da verdade durante edição
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML === '') {
            editorRef.current.innerHTML = content;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Quando o modal reabre com novo conteúdo (initialContent mudou),
    // o componente é remontado pelo ReferencesModal via key — sem necessidade de sync aqui

    const handleInput = useCallback(() => {
        if (editorRef.current) {
            setContent(editorRef.current.innerHTML);
        }
    }, [setContent]);

    // Salva a posição do cursor antes de clicar nos botões da toolbar
    const saveSelection = () => {
        const selection = window.getSelection();
        if (!selection || !editorRef.current || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const preRange = range.cloneRange();
        preRange.selectNodeContents(editorRef.current);
        preRange.setEnd(range.startContainer, range.startOffset);
        const start = preRange.toString().length;

        savedSelectionRef.current = {
            start,
            end: start + range.toString().length
        };
    };

    // Restaura o cursor após o foco voltar para o editor
    const restoreSelection = () => {
        if (!savedSelectionRef.current || !editorRef.current) return;

        const { start, end } = savedSelectionRef.current;
        const selection = window.getSelection();
        if (!selection) return;

        let charIndex = 0;
        const range = document.createRange();
        range.setStart(editorRef.current, 0);
        range.collapse(true);

        const nodeStack: Node[] = [editorRef.current];
        let node: Node | undefined;
        let foundStart = false;
        let stop = false;

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === Node.TEXT_NODE) {
                const next = charIndex + (node.textContent?.length || 0);
                if (!foundStart && start >= charIndex && start <= next) {
                    range.setStart(node, start - charIndex);
                    foundStart = true;
                }
                if (foundStart && end >= charIndex && end <= next) {
                    range.setEnd(node, end - charIndex);
                    stop = true;
                }
                charIndex = next;
            } else {
                Array.from(node.childNodes).reverse().forEach(child => nodeStack.push(child));
            }
        }

        selection.removeAllRanges();
        selection.addRange(range);
    };

    // Executa formatação garantindo foco e seleção corretos
    const execFormat = (command: string) => {
        if (!editorRef.current) return;
        saveSelection();
        editorRef.current.focus();
        restoreSelection();
        document.execCommand(command, false, undefined);
        handleInput();
    };

    return (
        <div className="refm-editor-container">
            <div className="refm-editor-toolbar">
                <button
                    type="button"
                    className="refm-editor-btn"
                    onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); }}
                    title="Negrito"
                >
                    <Bold size={15} />
                </button>

                <button
                    type="button"
                    className="refm-editor-btn"
                    onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); }}
                    title="Itálico"
                >
                    <Italic size={15} />
                </button>

                <button
                    type="button"
                    className="refm-editor-btn"
                    onMouseDown={(e) => { e.preventDefault(); execFormat('underline'); }}
                    title="Sublinhado"
                >
                    <Underline size={15} />
                </button>

                <div className="refm-editor-divider" />

                <button
                    type="button"
                    className="refm-editor-btn"
                    onMouseDown={(e) => { e.preventDefault(); execFormat('insertUnorderedList'); }}
                    title="Lista com marcadores"
                >
                    <List size={15} />
                </button>
            </div>

            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                data-placeholder={placeholder}
                className="refm-editor-content"
                suppressContentEditableWarning
            />
        </div>
    );
};